import React, { useState, useEffect, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import CssBaseline from '@mui/material/CssBaseline';
import getTheme from 'theme';
import AOS from 'aos';
import { NotificationProvider } from 'components/NotificationProvider';
import { CookieConsentProvider } from 'context/CookieConsentContext';
import CookieConsentBanner from 'components/CookieConsent';
import ChatWidgetButton from 'components/ChatWidgetButton';

export type ThemeMode = 'light' | 'dark';

export const useDarkMode = (): [ThemeMode, () => void, boolean] => {
  const [themeMode, setTheme] = useState<ThemeMode>('light');
  const [mountedComponent, setMountedComponent] = useState(false);

  const setMode = (mode: ThemeMode) => {
    try {
      window.localStorage.setItem('themeMode', mode);
    } catch {
      /* do nothing */
    }

    setTheme(mode);
  };

  const themeToggler = () => {
    themeMode === 'light' ? setMode('dark') : setMode('light');
  };

  useEffect(() => {
    try {
      const localTheme = window.localStorage.getItem(
        'themeMode',
      ) as ThemeMode | null;
      localTheme ? setTheme(localTheme) : setMode('light');
    } catch {
      setMode('light');
    }

    setMountedComponent(true);
  }, []);

  return [themeMode, themeToggler, mountedComponent];
};

interface PageProps {
  children: ReactNode;
}

export default function Page({ children }: PageProps) {
  React.useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement?.removeChild(jssStyles);
    }

    AOS.init({
      once: true,
      delay: 0,
      duration: 800,
      offset: 0,
      easing: 'ease-in-out',
    });
  }, []);

  const [themeMode, themeToggler, mountedComponent] = useDarkMode();

  useEffect(() => {
    AOS.refresh();
  }, [mountedComponent, themeMode]);

  return (
    <ThemeProvider theme={getTheme(themeMode, themeToggler)}>
      {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
      <CssBaseline />
      <CookieConsentProvider>
        <NotificationProvider>
          <Paper elevation={0}>{children}</Paper>
        </NotificationProvider>
        <CookieConsentBanner />
        <ChatWidgetButton />
      </CookieConsentProvider>
    </ThemeProvider>
  );
}
